import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import type { PdlLegacyCalcBar, PdlLegacyGridCalculationModel } from '../../lib/pdlLegacyGridCalculation';
import { PDL_LEGACY_CALC_COPY } from '../../lib/pdlAdjudicatorUiCopy';
import { PDL_SUPPORT_LABEL_SX } from '../../lib/pdlDecisionChrome';

/** MUI sx: numeric width/height 0–1 become % — use px strings for screen-reader-only sizing. */
const SR_ONLY_TABLE_SX = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function barWidthPct(amount: number, scaleMax: number): number {
  if (scaleMax <= 0) return 0;
  return Math.max(4, Math.min(100, (amount / scaleMax) * 100));
}

function barTone(bar: PdlLegacyCalcBar, theme: Theme): { fill: string; edge: string; text: string } {
  if (bar.kind === 'result') {
    return {
      fill: alpha(theme.palette.success.main, 0.22),
      edge: theme.palette.success.main,
      text: theme.palette.success.dark,
    };
  }
  if (bar.binding || bar.kind === 'cell_cap') {
    return {
      fill: alpha(theme.palette.warning.main, 0.22),
      edge: theme.palette.warning.main,
      text: theme.palette.warning.dark,
    };
  }
  if (bar.kind === 'income') {
    return {
      fill: alpha(theme.palette.info.main, 0.12),
      edge: alpha(theme.palette.info.main, 0.5),
      text: theme.palette.info.dark,
    };
  }
  return {
    fill: alpha(theme.palette.primary.main, 0.1),
    edge: alpha(theme.palette.primary.main, 0.4),
    text: theme.palette.text.primary,
  };
}

function FlowConnector({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.2, color: 'text.disabled' }} aria-hidden>
      <ArrowDownwardRoundedIcon
        sx={{
          fontSize: 18,
          animation: reducedMotion ? 'none' : 'pdl-legacy-flow 1.8s ease-in-out infinite',
          '@keyframes pdl-legacy-flow': {
            '0%, 100%': { transform: 'translateY(0)', opacity: 0.55 },
            '50%': { transform: 'translateY(2px)', opacity: 1 },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  );
}

function CalcBar3d({
  bar,
  scaleMax,
  reducedMotion,
}: {
  bar: PdlLegacyCalcBar;
  scaleMax: number;
  reducedMotion: boolean;
}) {
  const theme = useTheme();
  const tone = barTone(bar, theme);
  const widthPct = barWidthPct(bar.amount, scaleMax);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 38%) 1fr auto' },
        gap: { xs: 0.35, sm: 1 },
        alignItems: 'center',
        py: 0.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35, color: tone.text }}>
          {bar.label}
        </Typography>
        {bar.sublabel ? (
          <Typography variant="caption" sx={{ ...PDL_SUPPORT_LABEL_SX, display: 'block' }}>
            {bar.sublabel}
          </Typography>
        ) : null}
      </Box>

      {bar.kind === 'income' ? (
        <Typography
          variant="caption"
          sx={{
            ...PDL_SUPPORT_LABEL_SX,
            gridColumn: { xs: '1 / -1', sm: 'auto' },
            order: { xs: 3, sm: 0 },
            fontStyle: 'italic',
          }}
        >
          Input to table — not on the same scale as limits below
        </Typography>
      ) : (
        <Box
          sx={{
            minWidth: 0,
            gridColumn: { xs: '1 / -1', sm: 'auto' },
            order: { xs: 3, sm: 0 },
          }}
        >
          <Box sx={{ position: 'relative', height: 24 }} aria-hidden>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.divider, 0.35),
              }}
            />
            <Box
              sx={{
                width: `${widthPct}%`,
                height: '100%',
                borderRadius: 1,
                bgcolor: tone.fill,
                border: '2px solid',
                borderColor: tone.edge,
                boxShadow: bar.binding
                  ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.45)}`
                  : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.35)}`,
                transition: reducedMotion ? 'none' : 'width 0.3s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
          </Box>
        </Box>
      )}

      <Typography
        variant="body2"
        sx={{
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: tone.text,
          textAlign: { xs: 'left', sm: 'right' },
          gridColumn: { xs: '1 / -1', sm: 'auto' },
          order: { xs: 2, sm: 0 },
        }}
      >
        {fmt(bar.amount)}
      </Typography>
    </Box>
  );
}

function ProfileStat({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0, flex: 1, textAlign: 'center', px: 0.5 }}>
      <Typography variant="caption" sx={{ ...PDL_SUPPORT_LABEL_SX, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.35, mt: 0.15 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function PdlLegacyGridCalculationVisual({ model }: { model: PdlLegacyGridCalculationModel }) {
  const theme = useTheme();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const tableChip = model.profileChips.find((c) => c.startsWith('Table'));
  const bandChip = model.profileChips.find((c) => c.startsWith('Band'));
  const tierChip = model.profileChips.find((c) => c.startsWith('Tier'));

  const tableShort = tableChip?.match(/Table (\d+)/)?.[1] ?? '—';
  const bandShort = bandChip?.match(/Band (\d+)/)?.[1] ?? '—';
  const tierShort = tierChip?.match(/Tier (\d+)/)?.[1] ?? '—';

  return (
    <Box
      data-testid="pdl-legacy-grid-calculation"
      sx={{
        position: 'relative',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.35 }}>
          {PDL_LEGACY_CALC_COPY.title}
        </Typography>
        <Typography variant="caption" sx={{ ...PDL_SUPPORT_LABEL_SX, display: 'block', mt: 0.25 }}>
          {PDL_LEGACY_CALC_COPY.subtitle}
        </Typography>
        {model.formulaLine ? (
          <Typography
            variant="body2"
            sx={{
              mt: 0.6,
              fontFamily: 'ui-monospace, monospace',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {model.formulaLine}
          </Typography>
        ) : null}
      </Box>

      {model.profileChips.length > 0 ? (
        <Box
          sx={{
            px: 1.25,
            py: 0.85,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.6 }}>
            {PDL_LEGACY_CALC_COPY.profileTitle}
          </Typography>
          <Stack direction="row" spacing={0} divider={<Box sx={{ width: '1px', bgcolor: 'divider', mx: 0.5 }} />}>
            <ProfileStat label="Table" value={tableShort} />
            <ProfileStat label="Band" value={bandShort} />
            <ProfileStat label="Tier" value={tierShort} />
          </Stack>
        </Box>
      ) : null}

      <Box sx={{ px: 1, py: 0.75 }}>
        {model.bars.map((bar, index) => (
          <Box key={bar.id}>
            {index > 0 ? <FlowConnector reducedMotion={reducedMotion} /> : null}
            <CalcBar3d bar={bar} scaleMax={model.scaleMax} reducedMotion={reducedMotion} />
          </Box>
        ))}
      </Box>

      <Box component="table" sx={SR_ONLY_TABLE_SX} aria-label={PDL_LEGACY_CALC_COPY.title}>
        <caption>Legacy Cash4You pre-approval calculation</caption>
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {model.bars.map((bar) => (
            <tr key={bar.id}>
              <td>{bar.label}</td>
              <td>{fmt(bar.amount)}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
